import React, { useEffect, useState, useId } from 'react';
import { useOrganizationsStore } from '../../../store/admin/organizations/organizationsStore';
import { useGroupsStore } from '../../../store/admin/groups/groupsStore';
import { useLocation } from 'react-router-dom';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import AddLineIcon from 'remixicon-react/AddLineIcon';
import { useAppState } from '../../../store/appstate';
import { createGroup, listGroups } from '../../../api/groups/groups';
import usersActions from '../../../store/admin/users/actions';
import { removeApiSecret } from '../../../api/apiSecrets/apiSecrets';
import { createWebhook, listFailedWebhooks, listWebhooks } from '../../../api/webhooks/webhooks';
import {
    listOrganizations,
    enableOrganization,
    deleteOrganization,
    createOrganization,
} from '../../../api/organizations/organizations';
import { listApiSecrets, createApiSecret } from '../../../api/apiSecrets/apiSecrets';
import usePermissions from '../../../services/hooks/usePermissions';
import { useManagementTags } from '../../../services/hooks/useTags';
import { User } from '../../../types/users/User';
import CenterContent from '../../../components/atoms/Layout/CenterContent';
import HeaderContent from '../../../components/atoms/Layout/HeaderContent';
import { Modal } from '../../../components/atoms/Modal/Modal';
import InviteUserForm from '../../../components/molecules/Admin/Users/InviteUserForm';
import useModal from '../../../services/hooks/useModal';
import CreateNewGroupForm from '../../../components/molecules/Admin/Groups/CreateNewGroupForm';
import GroupSearchResults from '../../../components/molecules/Admin/Groups/GroupSearchResults';
import UserSearchResultsMain from '../../../components/molecules/Admin/Users/UserSearchResultsMain';
import SelectOrganization from '../../../components/molecules/Admin/SelectOrganization';
import { clearError, clearInviteUser } from '../../../store/admin/users/usersSlice';
import ApiSecretsSearchResults from '../../../components/molecules/Admin/ApiSecrets/ApiSecretsSearchResults';
import CreateApiSecretForm from '../../../components/molecules/Admin/ApiSecrets/CreateApiSecretForm';
import ConfirmationModal from '../../../components/atoms/Modal/ConfirmationModal';
import { ErrorType } from '../../../types/response/ErrorCodes';
import { useApiSecretsStore } from '../../../store/admin/apiSecrets/apiSecretsStore';
import { ApiSecret } from '../../../types/ApiSecret';
import MultiactionButton from '../../../components/atoms/Button/MultiactionButton';
import { LockedModal } from '../../../components/atoms/Modal/LockedModal';
import { useAppDispatch } from '../../../store';
import Button from '../../../components/atoms/Button/Button';
import { SessionState } from '../../../store/session/sessionSlice';
import Spinner from '../../../components/atoms/Spinner/Spinner';
import useLogTimeSpentOnPage from '../../../services/hooks/useLogTimeSpentOnPage';
import { config } from '../../../config';
import sessionActions from '../../../store/session/actions';
import Tabs, { TabContent } from '../../../components/atoms/Tabs/Tabs';
import Flex from '../../../components/atoms/Box/Flex';
import CreateWebhookForm from '../../../components/molecules/Admin/Webhooks/CreateWebhookForm';
import WebhooksSearchResultsMain from '../../../components/molecules/Admin/Webhooks/WebhooksSearchResultsMain';
import { useWebhooksStore } from '../../../store/admin/webhooks/webhooksStore';
import { listPendingUsers } from '../../../api/pendingUsers/pendingUsers';
import { usePendingUsersStore } from '../../../store/admin/pendingUsers/pendingUsersStore';
import CreateOrganizationForm from '../../../components/molecules/Admin/Organizations/CreateOrganizationForm';
import OnboardStoreForm from '../../../components/molecules/Admin/Stores/OnboardStoreForm';
import MerchantsSearchResults from '../../../components/molecules/Admin/Merchants/MerchantsSearchResults';
import AddMerchantForm from '../../../components/molecules/Admin/Merchants/AddMerchantForm';
import { useOnboardingStore } from '../../../store/admin/onboarding/onboardingStore';
import { getMerchantStatus, MerchantStatus } from '../../../types/onboarding/Merchant';
import { addMerchant, listMerchants, onboardStore } from '../../../api/onboarding/onboarding';
import { getOnboardingCountryName } from '../../../constants/onboardingCountries';
import Snackbar from '../../../components/atoms/Snackbar/Snackbar';
import { useIsFlagEnabledForOrg } from '../../../services/helpers/isFlagEnabled';

const STORE_ONBOARDING_FLAG = 'TURBO-2-merchant-store-onboarding';

interface ManageAccessProps {
    theme: DefaultTheme;
}

const ManageAccess: React.FC<ManageAccessProps> = ({ theme }: ManageAccessProps) => {
    document.title = 'MerchantHub - Manage Access';

    useLogTimeSpentOnPage('manageAccess');

    const dispatch = useAppDispatch();
    const { hash } = useLocation();
    const initialTabState = hash ? hash.substring(1) : 'users';
    const [tabState, setTabState] = useState(initialTabState);

    const [removeKey, setRemoveKey] = useState({ id: '', label: '' });
    const [onboardedStoreMessage, setOnboardedStoreMessage] = useState('');

    const {
        groupSearchResults,
        groupSearchStarted,
        groupSearchSucceeded,
        groupSearchFailed,
        createGroupStarted,
        createGroupSucceeded,
        createGroupFailed,
        resetCreateGroup,
    } = useGroupsStore();

    const users = useAppState<User[]>(s => s.admin.users.userSearchResults.data);

    const {
        userManagementGroupTags,
        userManagementUserTags,
        apiSecretTags,
        organizationTags,
        organizationStoreTags,
        webhooksTags,
    } = useManagementTags();

    const {
        isRemoving: isRemovingApiSecret,
        isRemoved: isRemovedApiSecret,
        removeError: removeErrorApiSecret,
        searchResults: { data: apiSecrets },
        isCreating: isCreatingApiSecret,
        createdCleared,
        removedCleared,
        searchStarted,
        searchSucceeded,
        searchFailed,
        creating,
        created,
        creatingFailed,
        removing,
        removed,
        removeFailed,
    } = useApiSecretsStore();

    const {
        searchResults: webhooks,
        searchFailedWebhooksResults: failedWebhooks,
        isCreating: isCreatingWebHook,
        createdCleared: createdClearedWebhooks,
        searchStarted: searchStartedWebhooks,
        searchSucceeded: searchSucceededWebhooks,
        searchFailed: searchFailedWebhooks,
        searchFailedWebhooksStarted,
        searchFailedWebhooksSucceeded,
        searchFailedWebhooksFailed,
        creating: creatingWebHook,
        creatingSucceeded,
        creatingFailed: creatingFailedWebHook,
    } = useWebhooksStore();

    const {
        searchResults: { data: pendingUsers },
        searchStarted: searchStartedPendingUsers,
        searchSucceeded: searchSucceededPendingUsers,
        searchFailed: searchFailedPendingUsers,
    } = usePendingUsersStore();

    const {
        selectedOrganization,
        selectedOrganizationPending,
        user: { isInternal } = { isInternal: false },
    } = useAppState<SessionState>(s => s.session);

    const groupPermissions = usePermissions(userManagementGroupTags);
    const userPermissions = usePermissions(userManagementUserTags);
    const apiSecretPermissions = usePermissions(apiSecretTags);
    const organizationPermissions = usePermissions(organizationTags);
    const storePermissions = usePermissions(organizationStoreTags);
    const webhooksPermissions = usePermissions(webhooksTags);

    const { isShown: isInviteUserShown, toggle: toggleInviteUser } = useModal();
    const { isShown: isAddGroupShown, toggle: toggleAddGroup } = useModal();
    const { isShown: isAddApiSecretShown, toggle: toggleAddApiSecret } = useModal();
    const { isShown: isRemoveApiSecretShown, toggle: toggleRemoveApiSecret } = useModal();
    const { isShown: isEnableOrganizationShown, toggle: toggleEnableOrganizationShown } =
        useModal();
    const { isShown: isDeleteOrganizationShown, toggle: toggleDeleteOrganizationShown } =
        useModal();
    const { isShown: isAddWebhookShown, toggle: toggleAddWebhook } = useModal();
    const { isShown: isCreateOrganizationShown, toggle: toggleCreateOrganization } = useModal();
    const { isShown: isOnboardStoreShown, toggle: toggleOnboardStore } = useModal();
    const { isShown: isAddMerchantShown, toggle: toggleAddMerchant } = useModal();

    const {
        merchants,
        listMerchantsStarted,
        listMerchantsSucceeded,
        listMerchantsFailed,
        isAddingMerchant,
        addedMerchant,
        addMerchantStarted,
        addMerchantSucceeded,
        addMerchantFailed,
        clearAddMerchant,
        isOnboardingStore,
        onboardedStore,
        onboardStoreStarted,
        onboardStoreSucceeded,
        onboardStoreFailed,
        clearOnboardStore,
        clearBankAccounts,
    } = useOnboardingStore();

    const {
        isEnablingOrganization,
        organizationEnabled,
        enableOrganizationError,
        getOrganizationsSucceeded,
        getOrganizationsFailed,
        enableOrganizationStarted,
        enableOrganizationSucceeded,
        enableOrganizationFailed,
        deleteOrganizationStarted,
        deleteOrganizationSucceeded,
        deleteOrganizationFailed,
        clearEnableOrganization,
        clearDeleteOrganization,
        isDeletingOrganization,
        isDeletedOrganization,
        deleteOrganizationError,
        deletedOrganization,
        isCreatingOrganization,
        createOrganizationStarted,
        createOrganizationSucceeded,
        createOrganizationFailed,
        addMerchantFailed: addFirstMerchantFailed,
        inviteAdministratorFailed,
        clearCreateOrganization,
    } = useOrganizationsStore();

    const onHideInviteUserModal = () => {
        dispatch(clearError());
        dispatch(clearInviteUser());
        toggleInviteUser();
    };

    const onHideCreateApiSecretModal = () => {
        if (isCreatingApiSecret) return;

        createdCleared();
        toggleAddApiSecret();
    };

    const onHideRemoveApiSecretModal = () => {
        removedCleared();
        toggleRemoveApiSecret();
    };

    const onHideEnableCheckoutApiModal = () => {
        clearEnableOrganization();
        toggleEnableOrganizationShown();
    };

    const onHideDeleteOrganizationModal = () => {
        clearDeleteOrganization();
        toggleDeleteOrganizationShown();
    };

    const onHideCreateOrganizationModal = () => {
        if (isCreatingOrganization) return;

        clearCreateOrganization();
        toggleCreateOrganization();
    };

    const onHideCreateWebhookModal = () => {
        if (isCreatingWebHook) return;

        createdClearedWebhooks();
        toggleAddWebhook();
    };

    const onHideOnboardStoreModal = () => {
        if (isOnboardingStore) return;

        clearOnboardStore();
        clearBankAccounts();
        toggleOnboardStore();
    };

    const onHideAddMerchantModal = () => {
        if (isAddingMerchant) return;

        clearAddMerchant();
        toggleAddMerchant();
    };

    const onToggleRemoveApiSecret = (e: React.MouseEvent<HTMLButtonElement>, key: ApiSecret) => {
        e.stopPropagation();
        setRemoveKey(key);
        toggleRemoveApiSecret();
    };

    const isNotCollectorOrganization = selectedOrganization.id != config.InternalOrganization;
    const isStoreOnboardingEnabled = useIsFlagEnabledForOrg(STORE_ONBOARDING_FLAG);
    const shouldLoadMerchants = storePermissions.hasRead || storePermissions.hasWrite;
    const hasMerchants = shouldLoadMerchants && merchants.length > 0;
    // Stores can only be onboarded for approved merchants (the backend rejects pending ones).
    const hasApprovedMerchant =
        hasMerchants &&
        merchants.some(merchant => getMerchantStatus(merchant) === MerchantStatus.Approved);
    const canOnboardStore =
        isStoreOnboardingEnabled && hasApprovedMerchant && storePermissions.hasWrite;
    // Adding merchants is a Merchant Services task: offered to internal users with organization write, which the
    // backend requires. It does not depend on the organization already having merchants: the Merchants tab appears
    // once the first one is added.
    const canAddMerchant =
        isInternal && isNotCollectorOrganization && organizationPermissions.hasWrite;

    // Show the new merchant where it landed, so the Merchants tab is open once the modal is closed.
    useEffect(() => {
        if (addedMerchant) setTabState('merchants');
    }, [addedMerchant]);

    useEffect(() => {
        if (!onboardedStore) return;

        const storeDescription = `${onboardedStore.presentationName} (${
            onboardedStore.salesSegment
        }, ${getOnboardingCountryName(onboardedStore.countryCode)})`;

        setOnboardedStoreMessage(
            onboardedStore.isPartiallyCreated
                ? `${storeDescription} is onboarded. Walley will finish a few settings before it is fully ready.`
                : `${storeDescription} is onboarded and ready for purchases.`
        );

        clearOnboardStore();
        if (isOnboardStoreShown) toggleOnboardStore();
        dispatch(sessionActions.getOrganization(selectedOrganization.id, true));
    }, [
        onboardedStore,
        clearOnboardStore,
        isOnboardStoreShown,
        toggleOnboardStore,
        dispatch,
        selectedOrganization.id,
    ]);

    useEffect(() => {
        if (isInternal) {
            listOrganizations(getOrganizationsSucceeded, getOrganizationsFailed);
        }

        const haveOrganization =
            !selectedOrganizationPending && selectedOrganization && selectedOrganization.id !== '';

        if (!haveOrganization) return;

        if (shouldLoadMerchants) {
            listMerchants(
                selectedOrganization.id,
                listMerchantsStarted,
                listMerchantsSucceeded,
                listMerchantsFailed
            );
        }

        if (groupPermissions.hasRead) {
            listGroups(
                selectedOrganization.id,
                groupSearchStarted,
                groupSearchSucceeded,
                groupSearchFailed
            );
        }

        if (userPermissions.hasRead) {
            dispatch(usersActions.listUsers(selectedOrganization.id));
        }

        if (userPermissions.hasRead) {
            listPendingUsers(
                selectedOrganization.id,
                searchStartedPendingUsers,
                searchSucceededPendingUsers,
                searchFailedPendingUsers
            );
        }

        if (apiSecretPermissions.hasRead) {
            listApiSecrets(selectedOrganization.id, searchStarted, searchSucceeded, searchFailed);
        }

        if (webhooksPermissions.hasRead) {
            listWebhooks(
                selectedOrganization.id,
                searchStartedWebhooks,
                searchSucceededWebhooks,
                searchFailedWebhooks
            );
            listFailedWebhooks(
                selectedOrganization.id,
                searchFailedWebhooksStarted,
                searchFailedWebhooksSucceeded,
                searchFailedWebhooksFailed
            );
        }
    }, [
        groupPermissions.hasRead,
        dispatch,
        selectedOrganization.id,
        userPermissions.hasRead,
        isInternal,
        selectedOrganizationPending,
        webhooksPermissions.hasRead,
        shouldLoadMerchants,
    ]);

    useEffect(() => {
        if (isDeletedOrganization)
            dispatch(sessionActions.setSelectedOrganization(config.InternalOrganization));
    }, [isDeletedOrganization]);

    const actionList = [
        {
            key: useId(),
            label: 'Invite',
            action: () => toggleInviteUser(),
            show: userPermissions.hasWrite && !selectedOrganization.blockInvites,
        },
        {
            key: useId(),
            label: 'Group',
            action: () => {
                resetCreateGroup();
                toggleAddGroup();
            },
            show: groupPermissions.hasWrite,
        },
        {
            key: useId(),
            label: 'Api key',
            action: () => toggleAddApiSecret(),
            show: apiSecretPermissions.hasWrite,
        },
        {
            key: useId(),
            label: 'Webhook',
            action: () => toggleAddWebhook(),
            show: webhooksPermissions.hasWrite,
        },
        {
            key: useId(),
            label: 'Store',
            action: () => toggleOnboardStore(),
            show: canOnboardStore,
        },
    ];
    const tabsList: TabContent[] = [
        { key: 'users', text: 'Users', isVisible: userPermissions.hasRead },
        { key: 'groups', text: 'Groups', isVisible: groupPermissions.hasRead },
        { key: 'merchants', text: 'Merchants', isVisible: hasMerchants },
        { key: 'apisecrets', text: 'API keys', isVisible: apiSecretPermissions.hasRead },
        {
            key: 'webhooks',
            text: 'Webhooks',
            isVisible: webhooksPermissions.hasRead,
        },
    ];

    return (
        <>
            <HeaderContent>
                <CenterContent>
                    <Row>
                        <Col>
                            <Label>Manage Organization</Label>
                            <BoldText>{selectedOrganization.name}</BoldText>
                        </Col>
                        {isInternal && isNotCollectorOrganization && (
                            <Col>
                                <Label>Organization Id</Label>
                                <BoldText>{selectedOrganization.id}</BoldText>
                            </Col>
                        )}
                        <Col>
                            <Label>Users</Label>
                            <UserRow>{users ? users.length : '-'}</UserRow>
                        </Col>
                        <Col>
                            <Label>Groups</Label>
                            <UserRow>
                                {groupSearchResults.data ? groupSearchResults.data.length : '-'}
                            </UserRow>
                        </Col>
                        <Actions>
                            {isInternal &&
                                !isNotCollectorOrganization &&
                                organizationPermissions.hasWrite && (
                                    <Button large onClick={() => toggleCreateOrganization()}>
                                        Create Organization
                                    </Button>
                                )}
                            {isInternal && isNotCollectorOrganization && (
                                <Button large onClick={() => toggleEnableOrganizationShown()}>
                                    Enable Checkout
                                </Button>
                            )}
                            {isInternal &&
                                isNotCollectorOrganization &&
                                organizationPermissions.hasWrite && (
                                    <Button large onClick={() => toggleDeleteOrganizationShown()}>
                                        Delete Organization
                                    </Button>
                                )}
                            {canAddMerchant && (
                                <Button
                                    large
                                    onClick={() => toggleAddMerchant()}
                                    data-testid="add-merchant-button"
                                >
                                    Add Merchant
                                </Button>
                            )}
                            {actionList.some(action => action.show) && (
                                <MultiactionButton actions={actionList}>
                                    Create new <AddLineIcon size={theme.icon.size.normal} />
                                </MultiactionButton>
                            )}
                            <SelectOrganization />
                        </Actions>
                    </Row>
                </CenterContent>
            </HeaderContent>

            <Page>
                {selectedOrganizationPending ? (
                    <PositionSpinner>
                        <Spinner loading={selectedOrganizationPending} />
                    </PositionSpinner>
                ) : (
                    <Flex column rowGap={'2rem'}>
                        <Tabs tabsList={tabsList} startTab={tabState} onTabChange={setTabState}>
                            {userPermissions.hasRead && (
                                <Flex key={'users'} column>
                                    <div translate="no">
                                        <UserSearchResultsMain
                                            hasWrite={userPermissions.hasWrite}
                                            users={users}
                                            pendingUsers={pendingUsers}
                                            toggleInvite={toggleInviteUser}
                                            organizationId={selectedOrganization.id}
                                        />
                                    </div>
                                </Flex>
                            )}
                            {groupPermissions.hasRead && (
                                <Flex key={'groups'} column>
                                    <div translate="no">
                                        <GroupSearchResults
                                            hasWrite={groupPermissions.hasWrite}
                                            data={groupSearchResults.data}
                                        />
                                    </div>
                                </Flex>
                            )}
                            {hasMerchants && (
                                <Flex key={'merchants'} column>
                                    <div translate="no">
                                        <MerchantsSearchResults
                                            organizationId={selectedOrganization.id}
                                            hasStoreWrite={storePermissions.hasWrite}
                                            hasOrganizationWrite={organizationPermissions.hasWrite}
                                        />
                                    </div>
                                </Flex>
                            )}
                            {apiSecretPermissions.hasRead && (
                                <Flex key={'apisecrets'} column>
                                    <div translate="no">
                                        <ApiSecretsSearchResults
                                            hasWrite={apiSecretPermissions.hasWrite}
                                            searchResults={apiSecrets}
                                            onRemove={onToggleRemoveApiSecret}
                                            toggleCreate={toggleAddApiSecret}
                                        />
                                    </div>
                                </Flex>
                            )}
                            {webhooksPermissions.hasRead && (
                                <Flex key={'webhooks'} column>
                                    <div translate="no">
                                        <WebhooksSearchResultsMain
                                            searchResults={webhooks}
                                            searchFailedWebhooksResults={failedWebhooks}
                                            toggleCreate={toggleAddWebhook}
                                        />
                                    </div>
                                </Flex>
                            )}
                        </Tabs>
                    </Flex>
                )}
            </Page>
            <Modal small isShown={isInviteUserShown} hide={onHideInviteUserModal}>
                <InviteUserForm
                    onInvite={(
                        organizationId: string,
                        email: string,
                        name: string,
                        selectedGroups?: string[]
                    ) => {
                        dispatch(
                            usersActions.inviteUserByEmail(
                                organizationId,
                                email,
                                name,
                                selectedGroups
                            )
                        );
                        setTabState('users');
                    }}
                    organizationId={selectedOrganization.id}
                    organizationName={selectedOrganization.name}
                    hide={onHideInviteUserModal}
                />
            </Modal>
            <Modal small isShown={isAddGroupShown} hide={toggleAddGroup}>
                <CreateNewGroupForm
                    onCreate={(organizationId: string, groupName: string) => {
                        createGroup(
                            organizationId,
                            groupName,
                            createGroupStarted,
                            createGroupSucceeded,
                            createGroupFailed,
                            groupSearchStarted,
                            groupSearchSucceeded,
                            groupSearchFailed
                        );
                    }}
                    organizationId={selectedOrganization.id}
                    organizationName={selectedOrganization.name}
                    hide={toggleAddGroup}
                />
            </Modal>
            <LockedModal
                dataTestId={'api-secret-modal'}
                focusLock
                small
                isShown={isAddApiSecretShown}
                hide={onHideCreateApiSecretModal}
            >
                <CreateApiSecretForm
                    onSave={(label: string, expirationDate: Date | null) => {
                        createApiSecret(
                            selectedOrganization.id,
                            label,
                            expirationDate,
                            creating,
                            created,
                            searchStarted,
                            searchSucceeded,
                            searchFailed,
                            creatingFailed
                        );
                        setTabState('apisecrets');
                    }}
                    hide={onHideCreateApiSecretModal}
                    locked={apiSecrets && apiSecrets.length >= selectedOrganization.maxApiKeys}
                />
            </LockedModal>
            <Modal medium isShown={isRemoveApiSecretShown} hide={onHideRemoveApiSecretModal}>
                <ConfirmationModal
                    title="Remove api secret"
                    label={removeKey.label}
                    labelAction={'Removed'}
                    action={() =>
                        removeApiSecret(
                            selectedOrganization.id,
                            removeKey.id,
                            removing,
                            removed,
                            searchStarted,
                            searchSucceeded,
                            searchFailed,
                            removeFailed
                        )
                    }
                    hideConfirmation={!isRemovingApiSecret && isRemovedApiSecret}
                    isProcessing={isRemovingApiSecret}
                    isSuccessful={isRemovedApiSecret}
                    hide={onHideRemoveApiSecretModal}
                    error={removeErrorApiSecret}
                    errorType={ErrorType.RemoveApiSecret}
                >
                    <>
                        Do you want to remove api secret with label <Id>{removeKey.label}</Id>
                    </>
                </ConfirmationModal>
            </Modal>

            <Modal medium isShown={isEnableOrganizationShown} hide={onHideEnableCheckoutApiModal}>
                <ConfirmationModal
                    title="Enable checkout API"
                    label={selectedOrganization.name}
                    labelAction={'Enabled organization'}
                    action={() =>
                        enableOrganization(
                            selectedOrganization.id,
                            enableOrganizationStarted,
                            enableOrganizationSucceeded,
                            enableOrganizationFailed
                        )
                    }
                    hideConfirmation={!isEnablingOrganization && organizationEnabled}
                    isProcessing={isEnablingOrganization}
                    isSuccessful={organizationEnabled}
                    hide={onHideEnableCheckoutApiModal}
                    error={enableOrganizationError}
                    errorType={ErrorType.EnableCheckoutApi}
                >
                    <>
                        Do you want to enable checkout API for organization{' '}
                        <b>{selectedOrganization.name}</b> with id <b>{selectedOrganization.id}</b>
                    </>
                </ConfirmationModal>
            </Modal>

            <Modal medium isShown={isDeleteOrganizationShown} hide={onHideDeleteOrganizationModal}>
                <ConfirmationModal
                    title="Delete organization"
                    label={deletedOrganization}
                    labelAction={'Deleted organization'}
                    action={() =>
                        deleteOrganization(
                            selectedOrganization.id,
                            selectedOrganization.name,
                            deleteOrganizationStarted,
                            deleteOrganizationSucceeded,
                            deleteOrganizationFailed
                        )
                    }
                    hideConfirmation={!isDeletingOrganization && isDeletedOrganization}
                    isProcessing={isDeletingOrganization}
                    isSuccessful={isDeletedOrganization}
                    hide={onHideDeleteOrganizationModal}
                    error={deleteOrganizationError}
                    errorType={ErrorType.DeleteOrganization}
                >
                    Do you want to delete organization <b>{selectedOrganization.name}</b> with id{' '}
                    <b>{selectedOrganization.id}</b>
                </ConfirmationModal>
            </Modal>
            <Modal small isShown={isCreateOrganizationShown} hide={onHideCreateOrganizationModal}>
                <CreateOrganizationForm
                    onCreate={request =>
                        createOrganization(
                            request,
                            createOrganizationStarted,
                            createOrganizationSucceeded,
                            createOrganizationFailed,
                            addFirstMerchantFailed,
                            inviteAdministratorFailed,
                            getOrganizationsSucceeded,
                            getOrganizationsFailed
                        )
                    }
                    hide={onHideCreateOrganizationModal}
                />
            </Modal>
            <Modal medium isShown={isAddWebhookShown} hide={onHideCreateWebhookModal}>
                <CreateWebhookForm
                    hide={onHideCreateWebhookModal}
                    onSave={data => {
                        createWebhook(
                            selectedOrganization.id,
                            data,
                            creatingWebHook,
                            creatingSucceeded,
                            creatingFailedWebHook,
                            searchStartedWebhooks,
                            searchSucceededWebhooks,
                            searchFailedWebhooks
                        );
                        setTabState('webhooks');
                    }}
                />
            </Modal>
            <LockedModal medium isShown={isOnboardStoreShown} hide={onHideOnboardStoreModal}>
                <OnboardStoreForm
                    organizationId={selectedOrganization.id}
                    organizationName={selectedOrganization.name}
                    groupCode={isInternal ? selectedOrganization.name : undefined}
                    onOnboard={request =>
                        onboardStore(
                            request,
                            onboardStoreStarted,
                            onboardStoreSucceeded,
                            onboardStoreFailed
                        )
                    }
                    hide={onHideOnboardStoreModal}
                />
            </LockedModal>
            <Modal small isShown={isAddMerchantShown} hide={onHideAddMerchantModal}>
                <AddMerchantForm
                    organizationName={selectedOrganization.name}
                    onAdd={request =>
                        addMerchant(
                            selectedOrganization.id,
                            request,
                            addMerchantStarted,
                            addMerchantSucceeded,
                            addMerchantFailed
                        )
                    }
                    hide={onHideAddMerchantModal}
                />
            </Modal>
            {onboardedStoreMessage && (
                <Snackbar
                    open
                    message={onboardedStoreMessage}
                    clear={() => setOnboardedStoreMessage('')}
                />
            )}
        </>
    );
};

const Page = styled(CenterContent)`
    padding-bottom: 4rem;
`;

const Id = styled.div`
    color: ${props => props.theme.colors.text.secondary};
`;
const PositionSpinner = styled.div`
    margin-top: 8rem;
`;

const Row = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-flow: row wrap;
    align-items: center;
    font-size: ${props => props.theme.text.size.medium};
`;

const UserRow = styled.div`
    display: flex;
    justify-content: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
    font-size: ${props => props.theme.text.size.medium};
`;
const Col = styled.div`
    display: flex;
    flex-direction: column;
    padding: ${props => `0 ${props.theme.layout.padding.medium}`};
    border-right: 0.2rem solid ${props => props.theme.colors.text.subtle};
    &:first-child {
        padding-left: 0;
        justify-content: center;
    }
    &:last-child {
        padding-right: 0;
        border-right: 0;
    }
`;
const Actions = styled.div`
    margin-left: auto;
    display: inline-flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;
`;
const Label = styled.div`
    font-size: ${props => props.theme.text.size.small};
    margin-bottom: 0.3rem;
    color: ${props => props.theme.colors.text.secondary};
`;

const BoldText = styled.span`
    font-family: ${props => props.theme.text.font.medium};
`;

export default withTheme(ManageAccess) as React.ComponentType<Omit<ManageAccessProps, 'theme'>>;
